import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotApprovalRequestListItem as ProtoListItem } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { listBiddingSpotApprovalRequests } from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import {
  ListBiddingSpotApprovalRequestsRequestSchema,
  type ListBiddingSpotApprovalRequestsResponse,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { APPROVAL_STATE_REFETCH_INTERVAL_MS } from '~/hooks/bidding/refetchInterval'

/**
 * スポット入札 承認依頼 一覧表示行型。
 */
export type SpotApprovalRequestRow = {
  id: string
  targetDate: ProtoDate | undefined
  approvalState: BiddingApprovalState
  bidCount: number
  createdBy: string | undefined
  createdAt: Timestamp | undefined
  updatedBy: string | undefined
  // optimistic lock 用に proto Timestamp で保持。表示時のみ timestampDate で Date 変換。
  updatedAt: Timestamp | undefined
}

function convertItem(item: ProtoListItem): SpotApprovalRequestRow {
  return {
    id: item.id,
    targetDate: item.targetDate,
    approvalState: item.approvalState,
    bidCount: item.bidCount,
    createdBy: item.createdBy,
    createdAt: item.createdAt,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
  }
}

export function convertListSpotResponse(
  resp: ListBiddingSpotApprovalRequestsResponse,
): SpotApprovalRequestRow[] {
  return resp.items.map(convertItem)
}

/**
 * スポット入札 承認依頼 一覧を取得する。
 *
 * fail-safe: global QueryClient の `throwOnError: true` を本 query だけ `false` に上書きし、
 * 取得失敗時も error boundary (ErrorLayout = 全画面エラー) に飛ばさない。 スポット入札画面は
 * 独立した複数 RPC (承認依頼 / ブロック / 調達必要量 / 入札制限 / 会社情報 …) の合成画面で、
 * 1 本の失敗で画面全体が消えると受渡日変更や他タブの参照までできなくなるため、 失敗した
 * データ源だけを画面上のエラーバナー (= 呼び出し元 page の `isError`) で知らせて描画は継続する。
 *
 * AC-BSP-096: 一覧 response は承認ステータス軸のみ (id / 受渡日 / approval_state /
 * 件数 / 監査情報) で明細 (価格・数量・札) を含まないため、 定期再取得しても編集中の
 * 下書きを巻き込まない。 承認者の承認・差戻し (AC-BSP-094) を実行者の画面へ手動
 * リロードなしで反映するため、 `APPROVAL_STATE_REFETCH_INTERVAL_MS` で polling する。
 */
export function useListBiddingSpotApprovalRequests(
  targetDateFilter?: ProtoDate,
  options?: {
    /**
     * false の間は RPC を発火しない (issue #7131 / AC-WFW-006a Decision 1:
     * ワークフローケース対象日が確定するまで対象日依存のデータ取得を開始しない)。
     * 省略時は従来どおり常時有効 (= 後方互換)。
     */
    enabled?: boolean
  },
): UseQueryResult<ListBiddingSpotApprovalRequestsResponse, ConnectError> {
  return useQuery(
    listBiddingSpotApprovalRequests,
    create(ListBiddingSpotApprovalRequestsRequestSchema, {
      targetDateFilter,
    }),
    {
      enabled: options?.enabled,
      refetchInterval: APPROVAL_STATE_REFETCH_INTERVAL_MS,
      throwOnError: false,
    },
  )
}
